import { Button, Collapse, Row, Col, Typography } from "antd";
import { RefreshIcon } from "components/Icons";
import Vocabulary from "components/Vocabulary";
import _ from "lodash";
import React, { useCallback, useState, useMemo } from "react";
import ModalUtil from "utils/modalUtil";
import { CellRenderers } from "utils/tableUtils";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { PermissionSetMap } from "constants/RBACConstants";
import AuthorisedContainer from "components/AuthorisedContainer";
import message from "components/CustomMessage";
import AppLoader from "components/AppLoader";
import ActionMenuV2 from "components/ActionMenuV2";
import LeanIXMappingForm from "../LeanIX/LeanIXMappingForm";
import LeanIXConfigForm from "./LeanIXConfigForm";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { isEmpty, isUndefined } from "utils/lodashUtils";
import LeanIXMappingsTable from "./LeanIXMappingsTable";
import useTriggerableEffect from 'react-triggerable-effect';
const Text = Typography.Text;

export default function LeanIXListing({
  loginConfigList,
  onUpdateConfigComponent,
  ...restProps
}) {
const [configCountData, setConfigCountData] = useState();
const getUpdatedConfigCount = useTriggerableEffect(()=>{
  const {promise, cancel} = ToolsIntegrationsAPI.getConfigCount();
  promise.then(setConfigCountData);
  return cancel;
}, [loginConfigList]);

const updateConfigComponent = useCallback(() => {
  onUpdateConfigComponent(true); 
  getUpdatedConfigCount();
}, [getUpdatedConfigCount, onUpdateConfigComponent]);

  const syncRepos = useCallback(async() => {
    try {
      await ToolsIntegrationsAPI.syncAsset("LEAN_IX").promise;
      message.success("Syncing assets, it may take some time.");
    } catch (e){
      console.log(e);
    }
  }, []);

  const loginConfigListWithCount = useMemo(() => {
    let data = [];
    if(!isEmpty(loginConfigList)){
      data = loginConfigList.map((config) => {
        const countData = isEmpty(configCountData) ? 0 : configCountData.find(d => d.configId === config.id);
        return {...config, countData}
      });
    }
    return data;
  }, [loginConfigList, configCountData]);

  return (
    isUndefined(loginConfigList) ? <AppLoader/> : 
    <>
    <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
        <Button type='default' size='small' className="m-l-auto m-y-sm" disabled={isEmpty(loginConfigList)} style={{display: "block"}} onClick={()=>syncRepos()}>
          <RefreshIcon /> Sync CMDB Resources
        </Button>
      </AuthorisedContainer>
      {loginConfigListWithCount.map(
        (loginConfig, i) => (
          <ConfigItem
            loginConfig={loginConfig}
            key={i}
            index={i}
            {...restProps}
            onUpdateConfigComponent={updateConfigComponent}
          />
        )
      )}
    </>
  );
}

function ConfigItem({ loginConfig, index, onUpdateConfigComponent, ...restProps }) {
  const onAddMapping = useCallback(
    async (data) => {
      ModalUtil.show({
        content: (
          <LeanIXMappingForm
            name={restProps.name}
            displayName={restProps.displayName}
            config={data}
            onUpdateConfigComponent={onUpdateConfigComponent}
            loginConfigId={loginConfig.id}
            afterClose={restProps.afterClose}
          />
        ),
      });
    },
    [
      loginConfig.id,
      restProps.name,
      restProps.displayName,
      restProps.afterClose,
      onUpdateConfigComponent
    ]
  );

  const editConfig = useCallback(() => {
    ModalUtil.show({
      content: (
        <LeanIXConfigForm
          displayName={restProps.displayName}
          name={restProps.name}
          config={loginConfig}
          onUpdateConfigComponent={onUpdateConfigComponent}
          afterClose={restProps.afterClose}
        />
      ),
    });
  }, [
    loginConfig,
    restProps.name,
    onUpdateConfigComponent,
    restProps.displayName,
    restProps.afterClose
  ]);

  const onDelete = useCallback(async () => {
    try {
      await ToolsIntegrationsAPI.deleteTicketSystemToolLoginDetails(
        "LEAN_IX",
        loginConfig.id
      ).promise;
      onUpdateConfigComponent();
      message.success("Configuration deleted successfully");
    } catch (err) {
      console.log(err);
    }
  }, [loginConfig.id, onUpdateConfigComponent]);

  const fetchFilters = useCallback(() => {
    let { promise, cancel } = ToolsIntegrationsAPI.getAssetFiltersByConfigId(loginConfig.id);
    promise = promise.then((data) => {
      return {
        ...data,
      };
    });
    return { promise, cancel };
  }, [loginConfig.id]);

  const fetchData = useCallback((params) => {
    const { filters } = params;
    const obj = {
      product: filters.product,
      subProduct: filters.subProduct,
      showMapped: true,
      toolName: "LEAN_IX",
      configId: loginConfig.id,
      ..._.omit(params, ["filters", "sortColumn", "sortOrder"]),
    };
    let { promise, cancel } = ToolsIntegrationsAPI.getAsset(obj);
    return { cancel, promise };
  }, [loginConfig.id]);

  const containsConfig = loginConfig.id === loginConfig.countData?.configId && loginConfig.countData?.configCount > 0;
  const deleteMessage =  containsConfig ? "Please delete the configurations of this account first" : "";
  const items = [
    {
      type: "edit",
      permission: PermissionSetMap.EDIT_TOOL_CONFIG,
    },
    {
      type: "delete",
      permission: PermissionSetMap.DELETE_TOOL,
      tooltip: deleteMessage,
      disabled: containsConfig,
    },
  ];
  const onClick = async (_, record) => {
    switch (record.name) {
      case "edit":
        editConfig();
        break;
      case "delete":
        await onDelete();
        break;
      default:
        break;
    }
  };
  return (
    <Collapse
      className="pull-based-tool"
      bordered={false}
      expandIconPosition="right"
    >
      <Collapse.Panel
        key={loginConfig.id}
        header={panelHeader(loginConfig, loginConfig.countData?.configCount)}
      >
        <div className="d-flex align-items-center justify-content-flex-end m-b-sm">
          <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Button size="small" type="primary" onClick={() => onAddMapping()}>
              Add Mapping
            </Button>
          </AuthorisedContainer>
          <div className="m-l-sm">
            <ActionMenuV2 items={items} onClick={onClick} />
          </div>
        </div>
          <ConfigTable
            loginConfig={loginConfig}
            {...restProps}
            onEdit={onAddMapping}
            fetchData={fetchData}
            fetchFilters={fetchFilters}
            onUpdateConfigComponent={onUpdateConfigComponent}
          />
      </Collapse.Panel>
    </Collapse>
  );
}

function ConfigTable({
  loginConfig,
  enabledAPI,
  deleteAPI,
  onUpdateConfigComponent,
  onEdit,
  fetchData,
  fetchFilters,
  ...restProps
}) {
  const onDelete = async (record) => {
    try {
      if (deleteAPI) {
        await deleteAPI(record.id).promise;
        onUpdateConfigComponent();
        message.success("Configuration deleted successfully.");
      }
    } catch (err) {
      console.log(err);
    }
  };
  const items = [
    {
      type: "edit",
      permission: PermissionSetMap.EDIT_TOOL_CONFIG,
    },
    {
      type: "delete",
      permission: PermissionSetMap.DELETE_TOOL,
    },
  ];
  const onClick = async (_, record) => {
    switch (record.name) {
      case "edit":
        let data = { ...record.data };
        onEdit(data);
        break;
      case "delete":
        await onDelete(record.data);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: CellRenderers.VALUE_OR_NA,
      width: 130,
    },
    {
      title: <Vocabulary name="product" />,
      dataIndex: "productName",
      filterKey: "product",
      key: "product",
      showFilter: true,
      render: (text) => {
        return <Text>{(text) || "All"}</Text>;
      },
      width: 130,
    },
    {
      title: <Vocabulary name="subProduct" />,
      dataIndex: "subProductName",
      filterKey: "subProduct",
      key: "subProduct",
      showFilter: true,
      render: (text) => {
        return <Text>{(text) || "All"}</Text>;
      },
      width: 130,
    },
    {
      title: "Environment",
      dataIndex: "environment",
      render: (environment) => <EnvironmentName name = {environment} />,
      width: 130,
    },
    // generateOperationalStatusColumn(data, 130),
    {
      title: "Action",
      key: "action",
      dataIndex: "id",
      width: 50,
      render: (id, obj) => {
        return <ActionMenuV2 items={items} onClick={onClick} data={obj} />;
      },
    },
  ];

  return (
    <LeanIXMappingsTable columns={columns} fetchData={fetchData} fetchFilters={fetchFilters} />
  );
}

export const panelHeader = (loginConfig, numberOfConfigs) => {
  const { url, name, userName, version } = loginConfig;

  return (
    <Row className="align-items-center">
      <Col span={20}>
        {url && <Typography.Text className="m-r-l">URL: {url}</Typography.Text>}
        {name && (
          <Typography.Text className="m-r-l">Name: {name}</Typography.Text>
        )}
        {userName && (
          <Typography.Text className="m-r-l">
            UserName: {userName}
          </Typography.Text>
        )}
        {version && (
          <Typography.Text className="m-r-l">
            API Version: {version}
          </Typography.Text>
        )}
        {
          <Typography.Text className="m-r-l">
            {!numberOfConfigs
              ? "No Configurations Available"
              : `Configurations: ${numberOfConfigs}`}
          </Typography.Text>
        }
      </Col>
    </Row>
  );
};
